import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const CODE_LENGTH = 6;
const RESEND_SECONDS = 60;

const INPUT_CLASS = 'w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground';
const BUTTON_CLASS = 'w-full bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
const LINK_BUTTON_CLASS = 'text-sm text-primary hover:underline disabled:no-underline disabled:opacity-50 disabled:cursor-not-allowed';

const normalizeCode = (value) => value.toUpperCase().replace(/[\s-]/g, '').slice(0, CODE_LENGTH);

const errorText = (result) =>
  result.attemptsLeft ? `${result.error} ${result.attemptsLeft} tries left.` : result.error;

// A notification email deep link sends signed-out readers here; `state.from`
// carries where they were headed so they land on that page instead of the feed.
function resolveDestination(user, from) {
  if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')) return from;
  const condo = user.condos?.[0];
  return condo ? `/${condo.slug || condo}` : '/';
}

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { requestCode, verifyCode, isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState(location.state?.email || searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [linkToken] = useState(searchParams.get('token'));
  const [step, setStep] = useState(linkToken ? 'link' : (location.state?.codeSent ? 'code' : 'email'));
  const [notice, setNotice] = useState(location.state?.message || null);
  const [error, setError] = useState(null);
  const [noAccount, setNoAccount] = useState(false);
  const [resendIn, setResendIn] = useState(location.state?.codeSent ? RESEND_SECONDS : 0);
  const [loading, setLoading] = useState(false);

  const from = location.state?.from;

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(resolveDestination(user, from));
    }
  }, [isAuthenticated, user, navigate, from]);

  useEffect(() => {
    if (linkToken) navigate('/login', { replace: true });
  }, [linkToken, navigate]);

  // The link only signs in after a click: email scanners that open links (and
  // even run their scripts) would otherwise spend the single-use token.
  const signInWithLink = async () => {
    setError(null);
    setLoading(true);
    const result = await verifyCode({ token: linkToken });
    setLoading(false);
    if (result.success) {
      navigate(resolveDestination(result.user));
      return;
    }
    setStep('email');
    setError(errorText(result));
  };

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const sendCode = async (e) => {
    if (e) e.preventDefault();
    setError(null);
    setNotice(null);
    setNoAccount(false);
    setLoading(true);

    const result = await requestCode(email.trim());
    if (result.success && result.user) {
      navigate(resolveDestination(result.user, from));
      return;
    }
    if (result.success && result.sent) {
      setStep('code');
      setCode('');
      setResendIn(RESEND_SECONDS);
    } else if (!result.success) {
      setNoAccount(!!result.noAccount);
      setError(errorText(result));
    }
    setLoading(false);
  };

  const checkCode = async (value) => {
    setError(null);
    setNotice(null);
    setLoading(true);
    const result = await verifyCode({ email: email.trim(), code: value });
    setLoading(false);
    if (result.success) {
      navigate(resolveDestination(result.user, from));
      return;
    }
    setError(errorText(result));
    setCode('');
  };

  const changeCode = (value) => {
    const normalized = normalizeCode(value);
    setCode(normalized);
    if (normalized.length === CODE_LENGTH && !loading) checkCode(normalized);
  };

  const changeEmail = () => {
    setStep('email');
    setCode('');
    setError(null);
    setNotice(null);
  };

  const messages = (
    <>
      {notice && (
        <div className="bg-primary/10 border border-primary text-foreground px-4 py-3 rounded">
          {notice}
        </div>
      )}
      {error && (
        <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded">
          {error}
          {noAccount && (
            <>
              {' '}
              <Link
                to={`/register?email=${encodeURIComponent(email.trim())}`}
                className="font-medium underline"
              >
                Sign up?
              </Link>
            </>
          )}
        </div>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-card rounded-lg shadow-xl p-8 border border-border">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {{ email: 'Welcome Back', code: 'Enter Your Code', link: 'Sign In' }[step]}
          </h1>
          <p className="text-muted-foreground">
            {step === 'email' && "Enter your email and we'll send you a code to sign in."}
            {step === 'link' && 'Click the button to finish signing in on this device.'}
            {step === 'code' && (
              <>
                We sent a code to <strong className="text-foreground">{email.trim()}</strong>. Enter it below to sign in.
              </>
            )}
          </p>
        </div>

        {step === 'link' && (
          <div className="space-y-6">
            {messages}
            <button type="button" onClick={signInWithLink} disabled={loading} className={BUTTON_CLASS}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>
        )}

        {step === 'email' && (
          <form onSubmit={sendCode} className="space-y-6" autoComplete="on">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={INPUT_CLASS}
                placeholder="you@example.com"
                autoComplete="email"
                required
                disabled={loading}
              />
            </div>

            {messages}

            <button type="submit" data-track="login-send-code" disabled={loading || !email.trim()} className={BUTTON_CLASS}>
              {loading ? 'Sending...' : 'Send Code'}
            </button>
          </form>
        )}

        {step === 'code' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              checkCode(code);
            }}
            className="space-y-6"
          >
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-foreground mb-2">
                Code
              </label>
              <input
                type="text"
                id="code"
                name="code"
                value={code}
                onChange={(e) => changeCode(e.target.value)}
                className={`${INPUT_CLASS} text-center text-2xl tracking-[0.5em] uppercase`}
                autoComplete="one-time-code"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                maxLength={CODE_LENGTH}
                autoFocus
                required
                disabled={loading}
              />
            </div>

            {messages}

            <button type="submit" disabled={loading || code.length !== CODE_LENGTH} className={BUTTON_CLASS}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        <div className="mt-6 text-center space-y-4">
          {step === 'email' && (
            <p className="text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline font-medium">
                Register here
              </Link>
            </p>
          )}
          {step === 'code' && (
            <div className="flex flex-col items-center space-y-2">
              <button
                type="button"
                onClick={() => sendCode()}
                disabled={loading || resendIn > 0}
                className={LINK_BUTTON_CLASS}
              >
                {resendIn > 0 ? `Resend code (${resendIn}s)` : 'Resend code'}
              </button>
              <button type="button" onClick={changeEmail} className={LINK_BUTTON_CLASS}>
                Use a different email
              </button>
            </div>
          )}
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground block">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
